import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Loader2, CheckCircle2, PartyPopper } from "lucide-react";

export default function AffiliateSignup() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [propName, setPropName] = useState("");

  useEffect(() => {
    api.get("/property").then((r) => setPropName(r.data?.name || "")).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/affiliates/signup", { ...form, origin_url: window.location.origin });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="signup-success">
          <div className="h-16 w-16 rounded-full bg-[#7A8C6D]/15 flex items-center justify-center mx-auto mb-5">
            <PartyPopper className="h-8 w-8 text-[#7A8C6D]" />
          </div>
          <h1 className="font-serif text-2xl text-[#2C3E35] mb-2">You're on the list!</h1>
          <p className="text-gray-600 text-sm">
            Thanks for wanting to partner with {propName || "us"}. We'll review your application and email you your
            personal referral link, QR code, and digital business card once you're approved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <img src="/icon-192.png" alt="MyAZbnb.com" className="h-16 w-16 rounded-2xl mx-auto mb-4 shadow" />
          <h1 className="font-serif text-3xl text-[#2C3E35]">Become an affiliate</h1>
          <p className="text-gray-600 text-sm mt-2">
            Earn <strong className="text-[#C05A45]">$50 for every booking</strong> you refer to {propName || "our rental"}.
            Sign up and we'll send you a personal link, QR code, and a digital business card to share.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl p-6 space-y-4" data-testid="signup-form">
          <div>
            <label className="text-sm font-medium text-[#2C3E35]">Full name</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith"
              className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C05A45]/40" data-testid="signup-name" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#2C3E35]">Email</label>
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@example.com"
              className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C05A45]/40" data-testid="signup-email" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#2C3E35]">Phone <span className="text-gray-400 font-normal">(optional)</span></label>
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 602 555 1234"
              className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C05A45]/40" data-testid="signup-phone" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#2C3E35]">How will you share it? <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={3} placeholder="e.g., social media, my golf group, local business…"
              className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C05A45]/40 resize-none" data-testid="signup-note" />
          </div>

          {error && <p className="text-sm text-red-600" data-testid="signup-error">{error}</p>}

          <button type="submit" disabled={submitting} data-testid="signup-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#C05A45] hover:bg-[#a94a37] transition-colors text-white font-semibold py-3.5 rounded-full disabled:opacity-60">
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
            {submitting ? "Submitting…" : "Sign me up"}
          </button>
          <p className="text-xs text-gray-400 text-center">Your application is reviewed before your link goes live.</p>
        </form>
      </div>
    </div>
  );
}
